import { apiFetch } from './client.ts';
import type { AppSetting, RoomOverrides } from './types.ts';

/**
 * MVP-472 follow-up 1. What a settings write may contain: any of the shop's own fields, and/or the
 * two values this screen's room may override, under their own `room` key.
 *
 * `room` is a partial of a partial on purpose — the screen writes one control at a time, and a key
 * left out is left alone server-side, while an explicit `null` (or a blank string) is how an
 * operator says "go back to using the shop's".
 */
export type SettingPatch = Partial<Omit<AppSetting, 'room'>> & {
  room?: Partial<Pick<RoomOverrides, 'stageTheme' | 'marqueeText'>>;
};

/**
 * `GET/PUT /api/setting` (SPEC AC-OPS-10: "every setting persisted to Strapi immediately with no
 * Save button").
 *
 * MVP-537: this used to say "Strapi's generated core-CRUD routes for the `setting` single type",
 * which was true when written — one row for the whole deployment, served by `createCoreRouter`.
 * Both halves are now the opposite. `setting` is a `collectionType`, one row per venue, since
 * MVP-468 slice 2 (`strapi-template/.../setting/schema.json:2`), and `playback-state` went the same
 * way per room in MVP-472. And the routes are hand-written precisely SO that these two paths
 * survived that change: the factory would have moved this endpoint to `/api/settings/:id`
 * (`strapi-template/src/api/setting/routes/setting.js:3-13`). So the client sees no `:id` and sends
 * no venue — the controller resolves which row is the caller's from their venue.
 *
 * That is also why `strapi-template/test/tenancy-kinds.test.js` exists: a flip back to `singleType`
 * would move no path and break no request here, it would silently collapse every venue onto one
 * shared row.
 *
 * Every write here is a partial patch merged server-side by Strapi's own update semantics —
 * callers only ever send the keys they changed.
 */
export async function getSettings(): Promise<AppSetting> {
  const res = await apiFetch<{ data: AppSetting }>('/api/setting');
  return res.data;
}

export async function updateSettings(patch: SettingPatch): Promise<AppSetting> {
  const res = await apiFetch<{ data: AppSetting }>('/api/setting', {
    method: 'PUT',
    body: { data: patch },
    retry: false, // a write must never be silently replayed
  });
  return res.data;
}
